/**
 * Clinic Admin — Contracts Management (Provider-owned legal documents)
 * ====================================================================
 * Sprint: TREATX-PROVIDER-CMS-CONTRACTS-01
 *
 * Canonical authority (ADR-006 LOCKED):
 *   - Collection: `legal_documents`
 *   - Owner: CLINIC
 *   - i18n: {en, tr} for title / description / blocking_message
 *   - Versions: append-only (POST /documents/{id}/versions). DELETE
 *     is server-blocked. Status transitions: active ↔ archived only.
 *
 * Endpoints used (DOC-RBAC-001 hardened):
 *   GET  /api/documents?clinic_id=&owner=CLINIC
 *   POST /api/documents
 *   PUT  /api/documents/{id}
 *   POST /api/documents/{id}/versions
 *
 * No new schema. No parallel architecture. No legacy `clinic_documents`.
 */
import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, Plus, FileText, History, Archive, ArchiveRestore,
  Edit, X, RefreshCw, Search, Globe, Stethoscope, AlertCircle,
  CheckCircle2, ShieldCheck, ListChecks,
} from 'lucide-react';

import { useClinicScope } from '../../../hooks/useClinicScope';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { toast } from '../../../hooks/use-toast';
import api from '../../../utils/api';

// ─────────────────────────────────────────────────────────────────────
// Static dictionaries
// ─────────────────────────────────────────────────────────────────────

const CATEGORIES = [
  { value: 'legal', label: 'Legal Agreement' },
  { value: 'treatment_consent', label: 'Treatment Consent' },
  { value: 'medical', label: 'Medical' },
  { value: 'financial', label: 'Financial Terms' },
  { value: 'privacy', label: 'Privacy / Data' },
  { value: 'instructions', label: 'Instructions' },
];

const REQUIRED_STAGES = [
  { value: 'OPTIONAL', label: 'Optional (any time)' },
  { value: 'pre_booking', label: 'Before booking' },
  { value: 'booking', label: 'At booking' },
  { value: 'pre_treatment', label: 'Before treatment' },
  { value: 'post_treatment', label: 'After treatment' },
];

// Curated patient-origin country codes (ISO-3166-1 alpha-2).
// Scope is advisory: empty list ⇒ applies to all countries.
const PATIENT_COUNTRY_OPTIONS = [
  { code: 'TR', name: 'Turkey' },
  { code: 'DE', name: 'Germany' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'ES', name: 'Spain' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'AT', name: 'Austria' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'PL', name: 'Poland' },
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'QA', name: 'Qatar' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'IR', name: 'Iran' },
  { code: 'IQ', name: 'Iraq' },
  { code: 'AZ', name: 'Azerbaijan' },
  { code: 'RU', name: 'Russia' },
  { code: 'UA', name: 'Ukraine' },
];

const CATEGORY_COLOR = {
  legal: 'bg-purple-100 text-purple-700',
  treatment_consent: 'bg-blue-100 text-blue-700',
  medical: 'bg-green-100 text-green-700',
  financial: 'bg-amber-100 text-amber-700',
  privacy: 'bg-slate-100 text-slate-700',
  instructions: 'bg-cyan-100 text-cyan-700',
};

// Title-display helper — accepts {en,tr} dict OR legacy string OR null.
const displayI18n = (value, lang = 'en') => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') return value[lang] || value.en || value.tr || Object.values(value)[0] || '';
  return '';
};

const formatScopeSummary = (treatments = [], countries = []) => {
  const t = treatments.length;
  const c = countries.length;
  if (t === 0 && c === 0) return 'All patients';
  const parts = [];
  if (t > 0) parts.push(`${t} treatment${t === 1 ? '' : 's'}`);
  if (c > 0) parts.push(`${c} ${c === 1 ? 'country' : 'countries'}`);
  return parts.join(' · ');
};

// ─────────────────────────────────────────────────────────────────────
// Create / Edit Modal
// ─────────────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  title: { en: '', tr: '' },
  description: { en: '', tr: '' },
  blocking_message: { en: '', tr: '' },
  category: 'legal',
  required_stage: 'OPTIONAL',
  is_required: false,
  requires_signature: false,
  is_blocking: false,
  treatment_types: [],
  patient_countries: [],
};

const ContractEditorModal = ({
  open,
  mode,            // 'create' | 'edit'
  initialDoc,
  treatmentOptions,
  clinicId,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_FORM);
  const [langTab, setLangTab] = useState('en');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && initialDoc) {
      // Lift the API doc into form shape (everything dict-shaped).
      const toI18n = (v) => {
        if (!v) return { en: '', tr: '' };
        if (typeof v === 'string') return { en: v, tr: '' };
        return { en: v.en || '', tr: v.tr || '' };
      };
      setForm({
        title: toI18n(initialDoc.title),
        description: toI18n(initialDoc.description),
        blocking_message: toI18n(initialDoc.blocking_message),
        category: initialDoc.category || 'legal',
        required_stage: initialDoc.required_stage || 'OPTIONAL',
        is_required: !!initialDoc.is_required,
        requires_signature: !!initialDoc.requires_signature,
        is_blocking: !!initialDoc.is_blocking,
        treatment_types: Array.isArray(initialDoc.treatment_types) ? [...initialDoc.treatment_types] : [],
        patient_countries: Array.isArray(initialDoc.patient_countries) ? [...initialDoc.patient_countries] : [],
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setLangTab('en');
    setError('');
  }, [open, mode, initialDoc]);

  if (!open) return null;

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  const setI18n = (k, lang, v) => setForm((prev) => ({ ...prev, [k]: { ...prev[k], [lang]: v } }));

  const toggleTreatment = (slug) => {
    setField(
      'treatment_types',
      form.treatment_types.includes(slug)
        ? form.treatment_types.filter((s) => s !== slug)
        : [...form.treatment_types, slug],
    );
  };

  const toggleCountry = (code) => {
    setField(
      'patient_countries',
      form.patient_countries.includes(code)
        ? form.patient_countries.filter((c) => c !== code)
        : [...form.patient_countries, code],
    );
  };

  const handleSubmit = async () => {
    setError('');

    // Validation: English title required (canonical default language).
    if (!form.title.en.trim()) {
      setError('English title is required.');
      setLangTab('en');
      return;
    }

    // Strip empty-string locales — server normalises {en: ""} to null.
    const cleanI18n = (obj) => {
      const out = {};
      Object.keys(obj || {}).forEach((k) => {
        if (typeof obj[k] === 'string' && obj[k].trim()) out[k] = obj[k];
      });
      return Object.keys(out).length ? out : null;
    };

    const payload = {
      title: cleanI18n(form.title),
      description: cleanI18n(form.description),
      blocking_message: cleanI18n(form.blocking_message),
      category: form.category,
      required_stage: form.required_stage,
      is_required: form.is_required,
      requires_signature: form.requires_signature,
      is_blocking: form.is_blocking,
      treatment_types: form.treatment_types,
      patient_countries: form.patient_countries,
    };

    setSubmitting(true);
    try {
      if (mode === 'create') {
        const res = await api.post('/documents', {
          owner: 'CLINIC',
          clinic_id: clinicId,
          ...payload,
        });
        if (res.data?.success) {
          toast({ title: 'Contract created', description: `${form.title.en} (v1.0) published.` });
          onSaved(res.data.document);
        } else {
          setError(res.data?.detail || 'Failed to create contract.');
        }
      } else {
        const res = await api.put(`/documents/${initialDoc.document_id}`, payload);
        if (res.data?.success) {
          toast({ title: 'Contract updated', description: 'Changes saved.' });
          onSaved(res.data.document);
        } else {
          setError(res.data?.detail || 'Failed to update contract.');
        }
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Request failed';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="contract-editor-modal"
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    >
      <Card className="w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between flex-shrink-0 border-b">
          <div>
            <CardTitle className="text-lg">
              {mode === 'create' ? 'Create new contract' : 'Edit contract'}
            </CardTitle>
            <p className="text-xs text-slate-500 mt-0.5">
              Versions are append-only. Saved changes apply to the current
              version metadata; to publish revised body content, use
              &quot;Publish new version&quot;.
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            data-testid="contract-editor-close"
          >
            <X className="h-5 w-5" />
          </Button>
        </CardHeader>

        <CardContent className="overflow-y-auto py-5 space-y-5">
          {error && (
            <div
              data-testid="contract-editor-error"
              className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm flex gap-2"
            >
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          {/* i18n tabs */}
          <div>
            <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
              Document body (i18n)
            </Label>
            <Tabs value={langTab} onValueChange={setLangTab} className="mt-2">
              <TabsList data-testid="contract-lang-tabs">
                <TabsTrigger value="en" data-testid="contract-lang-tab-en">
                  English
                </TabsTrigger>
                <TabsTrigger value="tr" data-testid="contract-lang-tab-tr">
                  Türkçe
                </TabsTrigger>
              </TabsList>

              {['en', 'tr'].map((lng) => (
                <TabsContent key={lng} value={lng} className="space-y-3 pt-3">
                  <div>
                    <Label>
                      Title {lng === 'en' && <span className="text-red-500">*</span>}
                    </Label>
                    <Input
                      data-testid={`contract-title-${lng}`}
                      value={form.title[lng]}
                      onChange={(e) => setI18n('title', lng, e.target.value)}
                      placeholder={lng === 'en' ? 'e.g., Surgical Consent Form' : 'örn. Cerrahi Onam Formu'}
                    />
                  </div>
                  <div>
                    <Label>{t('provider.contracts.form.description')}</Label>
                    <textarea
                      data-testid={`contract-description-${lng}`}
                      className="w-full min-h-[100px] p-3 border rounded-lg resize-none text-sm"
                      value={form.description[lng]}
                      onChange={(e) => setI18n('description', lng, e.target.value)}
                      placeholder={lng === 'en' ? 'Brief description of this document…' : 'Bu belgenin kısa açıklaması…'}
                    />
                  </div>
                  <div>
                    <Label>{t('provider.contracts.form.blockingMessage')}</Label>
                    <textarea
                      data-testid={`contract-blocking-${lng}`}
                      className="w-full min-h-[60px] p-3 border rounded-lg resize-none text-sm"
                      value={form.blocking_message[lng]}
                      onChange={(e) => setI18n('blocking_message', lng, e.target.value)}
                      placeholder={lng === 'en' ? 'Shown to the patient if this contract is required and unsigned' : 'Bu sözleşme zorunluysa ve imzalanmadıysa hastaya gösterilir'}
                    />
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </div>

          {/* Lifecycle / policy */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>{t('provider.contracts.form.category')}</Label>
              <select
                data-testid="contract-category-select"
                className="w-full p-2 border rounded-lg"
                value={form.category}
                onChange={(e) => setField('category', e.target.value)}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>{t('provider.contracts.form.requiredStage')}</Label>
              <select
                data-testid="contract-stage-select"
                className="w-full p-2 border rounded-lg"
                value={form.required_stage}
                onChange={(e) => setField('required_stage', e.target.value)}
              >
                {REQUIRED_STAGES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap gap-5">
            <div className="flex items-center gap-2">
              <Checkbox
                id="contract-is-required"
                data-testid="contract-is-required"
                checked={form.is_required}
                onCheckedChange={(v) => setField('is_required', !!v)}
              />
              <label htmlFor="contract-is-required" className="text-sm">{t('provider.contracts.form.isRequired')}</label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="contract-requires-sig"
                data-testid="contract-requires-signature"
                checked={form.requires_signature}
                onCheckedChange={(v) => setField('requires_signature', !!v)}
              />
              <label htmlFor="contract-requires-sig" className="text-sm">{t('provider.contracts.form.requiresSignature')}</label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="contract-blocking"
                data-testid="contract-is-blocking"
                checked={form.is_blocking}
                onCheckedChange={(v) => setField('is_blocking', !!v)}
              />
              <label htmlFor="contract-blocking" className="text-sm">{t('provider.contracts.form.isBlocking')}</label>
            </div>
          </div>

          {/* Scoping */}
          <div className="border rounded-lg p-4 space-y-4 bg-slate-50">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <ShieldCheck className="h-4 w-4 text-teal-600" />
              Scoping <span className="text-xs text-slate-400 font-normal">(empty = applies to all)</span>
            </div>

            <div>
              <Label className="text-xs flex items-center gap-1.5">
                <Stethoscope className="h-3 w-3" /> Treatment types
              </Label>
              <div
                data-testid="contract-treatments-grid"
                className="mt-2 max-h-40 overflow-y-auto border bg-white rounded p-2 grid grid-cols-2 md:grid-cols-3 gap-1.5"
              >
                {treatmentOptions.length === 0 && (
                  <p className="text-xs text-slate-400 col-span-full">{t('provider.contracts.loading.treatments')}</p>
                )}
                {treatmentOptions.map((t) => (
                  <label
                    key={t.slug}
                    className={`flex items-center gap-2 text-xs p-1.5 rounded cursor-pointer hover:bg-slate-50 ${
                      form.treatment_types.includes(t.slug) ? 'bg-teal-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      data-testid={`contract-treatment-${t.slug}`}
                      checked={form.treatment_types.includes(t.slug)}
                      onChange={() => toggleTreatment(t.slug)}
                    />
                    <span className="truncate">{t.display_name}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Selected: {form.treatment_types.length}
              </p>
            </div>

            <div>
              <Label className="text-xs flex items-center gap-1.5">
                <Globe className="h-3 w-3" /> Patient countries
              </Label>
              <div
                data-testid="contract-countries-grid"
                className="mt-2 max-h-40 overflow-y-auto border bg-white rounded p-2 grid grid-cols-2 md:grid-cols-3 gap-1.5"
              >
                {PATIENT_COUNTRY_OPTIONS.map((c) => (
                  <label
                    key={c.code}
                    className={`flex items-center gap-2 text-xs p-1.5 rounded cursor-pointer hover:bg-slate-50 ${
                      form.patient_countries.includes(c.code) ? 'bg-teal-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      data-testid={`contract-country-${c.code}`}
                      checked={form.patient_countries.includes(c.code)}
                      onChange={() => toggleCountry(c.code)}
                    />
                    <span className="truncate">
                      <span className="font-mono text-[10px] text-slate-400 mr-1">{c.code}</span>
                      {c.name}
                    </span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Selected: {form.patient_countries.length}
              </p>
            </div>
          </div>
        </CardContent>

        <div className="border-t px-6 py-4 flex justify-end gap-3 flex-shrink-0 bg-white">
          <Button variant="outline" onClick={onClose} data-testid="contract-editor-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="contract-editor-submit"
            className="bg-teal-600 hover:bg-teal-700"
          >
            {submitting ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving…
              </>
            ) : mode === 'create' ? (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Create contract
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Save changes
              </>
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Version History Modal (append-only)
// ─────────────────────────────────────────────────────────────────────

const VersionHistoryModal = ({ open, doc, onClose, onPublished }) => {
  const { t } = useTranslation();
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setNotes('');
  }, [open]);

  if (!open || !doc) return null;

  const handlePublish = async () => {
    if (!notes.trim()) {
      toast({ title: 'Notes required', description: 'Describe what changed in this version.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/documents/${doc.document_id}/versions`, {
        notes,
      });
      if (res.data?.success) {
        toast({ title: 'Version published', description: `v${res.data.document.current_version}` });
        onPublished(res.data.document);
      } else {
        toast({
          title: 'Failed to publish',
          description: res.data?.detail || 'Unknown error',
          variant: 'destructive',
        });
      }
    } catch (e) {
      toast({
        title: 'Failed to publish',
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const versions = Array.isArray(doc.versions) ? doc.versions : [];

  return (
    <div
      data-testid="contract-versions-modal"
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    >
      <Card className="w-full max-w-xl max-h-[85vh] overflow-hidden flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between flex-shrink-0 border-b">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <History className="h-5 w-5 text-teal-600" />
              Version history
            </CardTitle>
            <p className="text-xs text-slate-500 mt-0.5">
              {displayI18n(doc.title)} · {versions.length} version{versions.length === 1 ? '' : 's'}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="contract-versions-close">
            <X className="h-5 w-5" />
          </Button>
        </CardHeader>

        <CardContent className="overflow-y-auto py-4 space-y-3">
          {versions.map((v, idx) => (
            <div
              key={v.version}
              data-testid={`contract-version-${v.version}`}
              className={`p-4 rounded-lg border ${
                idx === 0 ? 'bg-teal-50 border-teal-200' : 'bg-white border-slate-200'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <Badge variant={idx === 0 ? 'default' : 'outline'} className={idx === 0 ? 'bg-teal-600' : ''}>
                  v{v.version} {idx === 0 && '(current)'}
                </Badge>
                <span className="text-xs text-slate-500">{v.upload_date}</span>
              </div>
              <p className="text-sm text-slate-700">{v.notes || '—'}</p>
              <p className="text-xs text-slate-400 mt-1">Uploaded by {v.uploaded_by}</p>
            </div>
          ))}
        </CardContent>

        {doc.status !== 'archived' && (
          <div className="border-t px-6 py-4 flex-shrink-0 bg-slate-50 space-y-3">
            <Label className="text-xs font-semibold text-slate-700">
              Publish new version (append-only)
            </Label>
            <textarea
              data-testid="contract-new-version-notes"
              className="w-full min-h-[60px] p-3 border rounded-lg resize-none text-sm bg-white"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('provider.contracts.form.versionNotesPlaceholder')}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose} data-testid="contract-versions-cancel">
                Close
              </Button>
              <Button
                onClick={handlePublish}
                disabled={submitting}
                data-testid="contract-publish-version"
                className="bg-teal-600 hover:bg-teal-700"
              >
                {submitting ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Publishing…
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Publish new version
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Archive / Restore confirmation
// ─────────────────────────────────────────────────────────────────────

const ArchiveConfirmModal = ({ open, doc, onClose, onConfirmed }) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  if (!open || !doc) return null;

  const isArchived = doc.status === 'archived';
  const nextStatus = isArchived ? 'active' : 'archived';

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const res = await api.put(`/documents/${doc.document_id}`, { status: nextStatus });
      if (res.data?.success) {
        toast({
          title: isArchived ? 'Contract restored' : 'Contract archived',
          description: `Status: ${nextStatus}`,
        });
        onConfirmed(res.data.document);
      } else {
        toast({
          title: 'Failed',
          description: res.data?.detail || 'Unknown error',
          variant: 'destructive',
        });
      }
    } catch (e) {
      toast({
        title: 'Failed',
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="contract-archive-modal"
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    >
      <Card className="w-full max-w-md">
        <CardHeader className="border-b">
          <CardTitle className="text-lg flex items-center gap-2">
            {isArchived ? (
              <>
                <ArchiveRestore className="h-5 w-5 text-emerald-600" />
                Restore contract?
              </>
            ) : (
              <>
                <Archive className="h-5 w-5 text-amber-600" />
                Archive contract?
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="py-5 space-y-3">
          <p className="text-sm text-slate-700">
            <span className="font-medium">{displayI18n(doc.title)}</span>
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
            <strong>{t('provider.contracts.banners.immutable')}</strong> — they can never be
            hard-deleted. Archiving hides the contract from active workflows
            but preserves every version for the audit trail. You can restore
            an archived contract at any time.
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose} data-testid="contract-archive-cancel">
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={submitting}
              data-testid="contract-archive-confirm"
              className={isArchived ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-600 hover:bg-amber-700'}
            >
              {submitting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Working…
                </>
              ) : isArchived ? (
                'Restore contract'
              ) : (
                'Archive contract'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────

const ContractsManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clinicId, userName } = useClinicScope();

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // 'active' | 'archived' | 'all'
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [treatmentOptions, setTreatmentOptions] = useState([]);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState('create');
  const [activeDoc, setActiveDoc] = useState(null);

  const [versionsOpen, setVersionsOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  // ── Loaders ────────────────────────────────────────────────────────

  const loadDocs = useCallback(async () => {
    if (!clinicId) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/documents', {
        params: { clinic_id: clinicId, owner: 'CLINIC' },
      });
      setDocs(Array.isArray(res.data?.documents) ? res.data.documents : []);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load contracts');
    } finally {
      setLoading(false);
    }
  }, [clinicId]);

  const loadTreatments = useCallback(async () => {
    try {
      const res = await api.get('/public/treatment-taxonomy');
      const list = Array.isArray(res.data?.treatments) ? res.data.treatments : [];
      // De-dupe + sort by display_name.
      const seen = new Set();
      const opts = [];
      list.forEach((t) => {
        if (!t?.slug || seen.has(t.slug)) return;
        seen.add(t.slug);
        opts.push({ slug: t.slug, display_name: t.display_name || t.slug });
      });
      opts.sort((a, b) => a.display_name.localeCompare(b.display_name));
      setTreatmentOptions(opts);
    } catch {
      setTreatmentOptions([]);
    }
  }, []);

  useEffect(() => {
    loadDocs();
    loadTreatments();
  }, [loadDocs, loadTreatments]);

  // ── Derived ────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs.filter((d) => {
      if (statusFilter !== 'all' && (d.status || 'active') !== statusFilter) return false;
      if (categoryFilter !== 'all' && d.category !== categoryFilter) return false;
      if (q) {
        const en = (displayI18n(d.title, 'en') || '').toLowerCase();
        const tr = (displayI18n(d.title, 'tr') || '').toLowerCase();
        if (!en.includes(q) && !tr.includes(q)) return false;
      }
      return true;
    });
  }, [docs, statusFilter, categoryFilter, search]);

  const stats = useMemo(() => {
    const active = docs.filter((d) => (d.status || 'active') === 'active').length;
    const archived = docs.filter((d) => d.status === 'archived').length;
    const versions = docs.reduce((acc, d) => acc + (Array.isArray(d.versions) ? d.versions.length : 0), 0);
    return { total: docs.length, active, archived, versions };
  }, [docs]);

  // ── Handlers ───────────────────────────────────────────────────────

  const openCreate = () => {
    setEditorMode('create');
    setActiveDoc(null);
    setEditorOpen(true);
  };

  const openEdit = (doc) => {
    setEditorMode('edit');
    setActiveDoc(doc);
    setEditorOpen(true);
  };

  const openVersions = (doc) => {
    setActiveDoc(doc);
    setVersionsOpen(true);
  };

  const openArchive = (doc) => {
    setActiveDoc(doc);
    setArchiveOpen(true);
  };

  const upsertLocal = (newDoc) => {
    setDocs((prev) => {
      const idx = prev.findIndex((d) => d.document_id === newDoc.document_id);
      if (idx === -1) return [...prev, newDoc];
      const copy = [...prev];
      copy[idx] = newDoc;
      return copy;
    });
  };

  // ── Render ─────────────────────────────────────────────────────────

  if (!clinicId) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-8 w-8 text-amber-600 mx-auto" />
          <p className="mt-3 text-slate-700">{t('provider.contracts.banners.clinicNotResolved')}</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/clinic/admin')}>
            Back to dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="contracts-management-page" className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-teal-800 to-cyan-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <Button
                variant="ghost"
                onClick={() => navigate('/clinic/admin')}
                className="text-teal-200 hover:text-white mb-2"
                data-testid="contracts-back-btn"
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Back to Dashboard
              </Button>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <ListChecks className="h-6 w-6" />
                Contracts
              </h1>
              <p className="text-teal-200 text-sm">
                Provider-owned legal documents · {userName || 'Admin'}
              </p>
            </div>
            <Button
              onClick={openCreate}
              data-testid="contracts-new-btn"
              className="bg-white text-teal-700 hover:bg-teal-50"
            >
              <Plus className="h-4 w-4 mr-2" /> New contract
            </Button>
          </div>
        </div>
      </header>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="bg-white shadow-md">
            <CardContent className="p-4 text-center">
              <p data-testid="contracts-stat-total" className="text-3xl font-bold text-slate-900">{stats.total}</p>
              <p className="text-sm text-slate-500">{t('provider.contracts.stats.total')}</p>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-md">
            <CardContent className="p-4 text-center">
              <p data-testid="contracts-stat-active" className="text-3xl font-bold text-emerald-600">{stats.active}</p>
              <p className="text-sm text-slate-500">{t('provider.contracts.stats.active')}</p>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-md">
            <CardContent className="p-4 text-center">
              <p data-testid="contracts-stat-archived" className="text-3xl font-bold text-amber-600">{stats.archived}</p>
              <p className="text-sm text-slate-500">{t('provider.contracts.stats.archived')}</p>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-md">
            <CardContent className="p-4 text-center">
              <p data-testid="contracts-stat-versions" className="text-3xl font-bold text-teal-600">{stats.versions}</p>
              <p className="text-sm text-slate-500">{t('provider.contracts.stats.totalVersions')}</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <Card>
          <CardContent className="p-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="contracts-search-input"
                placeholder={t('provider.contracts.search.placeholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              data-testid="contracts-status-filter"
              className="border rounded-lg px-3 py-2 text-sm bg-white"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="active">{t('provider.contracts.stats.active')}</option>
              <option value="archived">{t('provider.contracts.stats.archived')}</option>
              <option value="all">All</option>
            </select>
            <select
              data-testid="contracts-category-filter"
              className="border rounded-lg px-3 py-2 text-sm bg-white"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="all">{t('provider.contracts.search.allCategories')}</option>
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
            <Button
              variant="outline"
              size="sm"
              onClick={loadDocs}
              data-testid="contracts-refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4 mb-12">
        <Card>
          <CardContent className="p-0">
            {error && (
              <div className="p-4 bg-red-50 border-b border-red-200 text-red-700 text-sm flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}
            {loading ? (
              <div className="p-12 text-center text-slate-500">
                <RefreshCw className="h-6 w-6 animate-spin mx-auto" />
                <p className="mt-2 text-sm">{t('provider.contracts.loading.contracts')}</p>
              </div>
            ) : filtered.length === 0 ? (
              <div data-testid="contracts-empty-state" className="p-12 text-center">
                <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 font-medium">{t('provider.contracts.empty.noMatches')}</p>
                <p className="text-sm text-slate-400 mt-1">
                  Click &quot;New contract&quot; to create your first one.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-4 font-semibold text-slate-600">{t('provider.contracts.columns.title')}</th>
                      <th className="text-left p-4 font-semibold text-slate-600">{t('provider.contracts.form.category')}</th>
                      <th className="text-left p-4 font-semibold text-slate-600">{t('provider.contracts.columns.version')}</th>
                      <th className="text-left p-4 font-semibold text-slate-600">{t('provider.contracts.columns.scope')}</th>
                      <th className="text-left p-4 font-semibold text-slate-600">{t('provider.contracts.columns.flags')}</th>
                      <th className="text-left p-4 font-semibold text-slate-600">{t('provider.contracts.columns.status')}</th>
                      <th className="text-right p-4 font-semibold text-slate-600">{t('provider.contracts.columns.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((doc) => {
                      const archived = doc.status === 'archived';
                      return (
                        <tr
                          key={doc.document_id}
                          data-testid={`contract-row-${doc.document_id}`}
                          className="border-b hover:bg-slate-50 transition-colors"
                        >
                          <td className="p-4">
                            <div className="flex items-start gap-3">
                              <div className="w-9 h-9 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <FileText className="h-4 w-4 text-slate-500" />
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-slate-800 truncate">
                                  {displayI18n(doc.title) || <em className="text-slate-400">{t('provider.contracts.titles.untitled')}</em>}
                                </p>
                                {displayI18n(doc.title, 'tr') && (
                                  <p className="text-xs text-slate-400 truncate">
                                    TR: {displayI18n(doc.title, 'tr')}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="p-4">
                            <Badge className={CATEGORY_COLOR[doc.category] || 'bg-slate-100 text-slate-700'}>
                              {CATEGORIES.find((c) => c.value === doc.category)?.label || doc.category}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <button
                              onClick={() => openVersions(doc)}
                              data-testid={`contract-version-btn-${doc.document_id}`}
                              className="text-teal-700 hover:text-teal-900 font-medium inline-flex items-center gap-1"
                            >
                              v{doc.current_version || '1.0'}
                              <History className="h-3.5 w-3.5" />
                            </button>
                            <p className="text-xs text-slate-400">
                              {(doc.versions?.length || 0)} version{(doc.versions?.length || 0) === 1 ? '' : 's'}
                            </p>
                          </td>
                          <td className="p-4 text-xs text-slate-600">
                            {formatScopeSummary(doc.treatment_types, doc.patient_countries)}
                          </td>
                          <td className="p-4">
                            <div className="flex flex-col gap-1">
                              {doc.is_required && (
                                <Badge className="bg-red-100 text-red-700 w-fit text-[10px]">{t('provider.contracts.form.isRequired')}</Badge>
                              )}
                              {doc.requires_signature && (
                                <Badge className="bg-blue-100 text-blue-700 w-fit text-[10px]">{t('provider.contracts.flags.signature')}</Badge>
                              )}
                              {doc.is_blocking && (
                                <Badge className="bg-amber-100 text-amber-700 w-fit text-[10px]">{t('provider.contracts.form.isBlocking')}</Badge>
                              )}
                            </div>
                          </td>
                          <td className="p-4">
                            <Badge
                              className={
                                archived
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-emerald-100 text-emerald-700'
                              }
                            >
                              {archived ? 'Archived' : 'Active'}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEdit(doc)}
                                disabled={archived}
                                title={archived ? 'Restore before editing' : 'Edit'}
                                data-testid={`contract-edit-${doc.document_id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openVersions(doc)}
                                title={t('provider.contracts.aria.versionHistory')}
                                data-testid={`contract-versions-${doc.document_id}`}
                              >
                                <History className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openArchive(doc)}
                                title={archived ? 'Restore' : 'Archive'}
                                data-testid={`contract-archive-${doc.document_id}`}
                                className={archived ? 'text-emerald-600' : 'text-amber-600'}
                              >
                                {archived ? (
                                  <ArchiveRestore className="h-4 w-4" />
                                ) : (
                                  <Archive className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      <ContractEditorModal
        open={editorOpen}
        mode={editorMode}
        initialDoc={activeDoc}
        treatmentOptions={treatmentOptions}
        clinicId={clinicId}
        onClose={() => setEditorOpen(false)}
        onSaved={(d) => {
          upsertLocal(d);
          setEditorOpen(false);
        }}
      />
      <VersionHistoryModal
        open={versionsOpen}
        doc={activeDoc}
        onClose={() => setVersionsOpen(false)}
        onPublished={(d) => {
          upsertLocal(d);
          setActiveDoc(d);
        }}
      />
      <ArchiveConfirmModal
        open={archiveOpen}
        doc={activeDoc}
        onClose={() => setArchiveOpen(false)}
        onConfirmed={(d) => {
          upsertLocal(d);
          setArchiveOpen(false);
        }}
      />
    </div>
  );
};

export default ContractsManagement;
